import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Tab, Tabs, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { isOtherCustomer } from '../utils/auth';
import { ErrorExplorer } from '../components/insights/ErrorExplorer';
import { SpendView } from '../components/insights/SpendView';
import { LearningImpact } from '../components/insights/LearningImpact';

const TABS = [
  { value: 'errors', label: 'Errors' },
  { value: 'spend', label: 'Spend' },
  { value: 'learning', label: 'Self-improvement' },
] as const;
type TabValue = (typeof TABS)[number]['value'];

const isTab = (v: string | null): v is TabValue => TABS.some(t => t.value === v);

export const Insights: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions } = useAuth();
  const param = searchParams.get('customer_id');
  const customerId = isOtherCustomer(param, permissions) ? param : null;
  const requested = searchParams.get('tab');
  const tab: TabValue = isTab(requested) ? requested : 'errors';

  const selectTab = (value: TabValue) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Box>
      <Box sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Insights
        </Typography>
        <Typography variant="body2" color="text.secondary">
          What's failing, what it costs, and whether self-improvement is helping, across all your functions and chains.
        </Typography>
      </Box>
      {customerId && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Viewing account {customerId}.
        </Alert>
      )}
      <Tabs value={tab} onChange={(_, v: TabValue) => selectTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {TABS.map(t => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>
      {tab === 'errors' && <ErrorExplorer customerId={customerId} />}
      {tab === 'spend' && <SpendView customerId={customerId} />}
      {tab === 'learning' && <LearningImpact customerId={customerId} />}
    </Box>
  );
};

export default Insights;
